export enum ActivityCreationTypeIdEnum {
  MANUAL = 1,
  AUTOMATIC = 2,
  CLONED = 3,
  PRELOADED = 4,
  PAST_YEAR = 5,
}

export enum QuestionSelectionEnum {
  MANUAL = ActivityCreationTypeIdEnum.MANUAL,
  AUTOMATIC = ActivityCreationTypeIdEnum.AUTOMATIC,
}

export enum ActivityStatusEnum {
  DRAFT = 1,
  FINALIZED = 2,
  PUBLISHED = 3,
  EXPIRED = 4,
}

export enum PredesignedActivityTagsEnum {
  CHAPTER_BEGINNER = 21,
  CHAPTER_CHALLENGER = 22,
  CHAPTER_ADVANCED = 23,
  CONCEPT_QUIZ = 24,
  INTERACTIVE_VIDEO = 25,
}

export enum ActivityInstanceStatusEnum {
  UPCOMING = 1,
  PENDING = 2,
  STARTED = 3,
  COMPLETED = 4,
  EXPIRED = 5,
}

export enum SelfTestInstanceStatusEnum {
  PENDING = ActivityInstanceStatusEnum.PENDING,
  STARTED = ActivityInstanceStatusEnum.STARTED,
  COMPLETED = ActivityInstanceStatusEnum.COMPLETED,
}

export enum PredesignedInstanceStatusEnum {
  NEW = ActivityInstanceStatusEnum.UPCOMING,
  PENDING = ActivityInstanceStatusEnum.PENDING,
  STARTED = ActivityInstanceStatusEnum.STARTED,
  COMPLETED = ActivityInstanceStatusEnum.COMPLETED,
  EXPIRED = ActivityInstanceStatusEnum.EXPIRED,
}

export enum QuestionStatusEnum {
  UNSEEN = "unseen",
  UNANSWERED = "unanswered",
  ANSWERED = "answered",
  MARKED_FOR_REVIEW = "markedForReview",
  CURRENT_QUESTION = "currentQuestion",
  CORRECT = "correct",
  INCORRECT = "incorrect",
  SKIPPED = "skipped",
}

export enum EventTypesEnum {
  START_ACTIVITY = "start_activity",
  RESUME_ACTIVITY = "resume_activity",
  VIEW_QUESTION = "view_question",
  SELECT_ANSWER = "select_answer",
  INPUT_ANSWER = "input_answer",
  UPLOAD_ANSWER = "upload_answer",
  CLEAR_ANSWER = "clear_answer",
  SUBMIT_ANSWER = "submit_attempt",
  MARK_FOR_REVIEW = "mark_for_review",
  VIEW_QUESTIONS = "view_questions",
  VIEW_QUESTIONS_CLOSE = "view_questions_close",
  INSTRUCTIONS_CONFIRMED = "instructions_confirmed",
  VIEW_INSTRUCTIONS = "view_instructions",
  VIEW_INSTRUCTIONS_CLOSE = "view_instructions_close",
  FULLSCREEN_DISABLED = "fullscreen_disabled",
  END_ACTIVITY_POPUP_OPEN = "end_activity_popup_open",
  END_ACTIVITY_POPUP_CLOSE = "end_activity_popup_close",
  USER_END_ACTIVITY = "user_end_activity",
  END_ACTIVITY_SUCCESSFUL = "end_activity_successful",
  AUTO_END_ACTIVITY_TIMER = "auto_end_activity_timer",
  AUTO_END_ACTIVITY_FULLSCREEN = "auto_end_activity_fullscreen",
  VIDEO_PLAY = "video_play",
  VIDEO_PAUSE = "video_pause",
  VIDEO_SEEK = "video_seek",
  VIDEO_SECTION_REACHED = "video_section_reached",
  VIDEO_SECTION_COMPLETED = "video_section_completed",
  VIDEO_COMPLETED = "video_completed",
}

export enum StudentActivityReportSegmentEnum {
  SUMMARY = "Summary",
  QUESTIONS = "Questions",
  INSIGHTS = "Insights",
}

export enum SubjectiveSubmissionStatusEnum {
  VIEW_SOLUTION = 1,
  ACCEPT_SOLUTION = 2,
  NO_SOLUTION = 3,
}

// LEGACY compound classification. Retained only as the key of
// LEGACY_TYPE_DECOMPOSITION (constants/activity.ts) and as a page-intent token
// for authoring entry points. Do NOT compare server-returned values against
// this enum — the backend now sends the four-axis values below.
export enum ActivityTypeIdsEnum {
  SELF_PRACTICE = 5,
  SELF_TEST = 6,
  INSTITUTE_TEST = 7,
  ASSIGNMENT = 8,
  SELF_ASSIGNMENT = 9,
  STUDENT_PRELOADED_TEST = 201,
  INSTITUTE_PRELOADED_TEST = 202,
  INTERNAL_PRELOADED_TEST = 203,
  PAST_YEAR_PAPER = 301,
  STUDENT_TEST_SERIES = 401,
  INSTITUTE_TEST_SERIES = 402,
  STUDENT_OLYMPIAD_TEST = 501,
  INSTITUTE_OLYMPIAD_TEST = 502,
  WORKBOOK_EXERCISE = 601,
  STUDENT_PRELOADED_ASSIGNMENT = 801,
  INSTITUTE_PRELOADED_ASSIGNMENT = 802,
  INTERNAL_PRELOADED_ASSIGNMENT = 803,
  INTERACTIVE_VIDEO = 901,
}

// Four-axis activity classification (backend /v7). These are the values the
// backend sends/expects on every create, read and filter request. Use the
// single-axis predicate helpers in methods/activity.ts to branch on them.
export enum ActivityTypeEnum {
  PRACTICE = 1,
  ASSIGNMENT = 2,
  TEST = 3,
  INTERACTIVE_VIDEO = 4,
  WORKBOOK = 5,
}

export enum ActivityScopeEnum {
  STUDENT = 1,
  INSTITUTE = 2,
  INTERNAL = 3,
}

export enum ActivityCategoryEnum {
  DEFAULT = 1,
  PREDESIGNED = 2,
  PAST_YEAR = 3,
  SERIES = 4,
  OLYMPIAD = 5,
}

export enum ActivitySubtypeEnum {
  CHAPTER_BEGINNER = 1,
  CHAPTER_CHALLENGER = 2,
  CHAPTER_ADVANCED = 3,
  CONCEPT_CHECK = 4,
}

export enum OfflineActivityStatusEnum {
  PENDING = 1,
  PROCESSED = 2,
  FINALIZED = 3,
}

export enum OmrUploadStatusEnum {
  PENDING = 1,
  PROCESSED = 2,
  FAILED = 3,
}

export enum OmrStatusEnum {
  NEW = 1,
  APPROVED = 2,
  REVIEW = 3,
  REJECTED = 4,
  FAILED = 5,
}

export enum CreateActivityDesignSegmentEnum {
  TEMPLATES = "Templates",
  ACTIVITY_DESIGNER = "Activity Designer",
}

export enum ActivityDesignerStepsEnum {
  ACTIVITY_CONTENT = "Content",
  BASIC_INFORMATION = "Basic Information",
  ACTIVITY_SECTIONS = "Activity Sections",
  ADDITIONAL_DETAILS = "Additional Details",
}

export enum SectionGenerationModeEnum {
  SUBJECT_QUESTION_TYPE = 1,
  ONLY_SUBJECT = 2,
  ONLY_QUESTION_TYPE = 3,
}

export enum SectionOrderingEnum {
  SUBJECT_QUESTION_TYPE = 1,
  QUESTION_TYPE_SUBJECT = 2,
}

export enum SectionNamingEnum {
  SUBJECT_QUESTION_TYPE = 1,
  SUBJECT_LETTER = 2,
  QUESTION_TYPE_LETTER = 3,
  SUBJECT = 4,
  QUESTION_TYPE = 5,
  SECTION_LETTER = 6,
  SECTION_NUMBER = 7,
}

export enum TestTagsEnum {
  QUIZ = 1,
  CHAPTER_TEST = 2,
  FORMATIVE_ASSESSMENT = 3,
  HALF_YEARLY = 4,
  FULL_SYLLABUS = 5,
}

export enum AssignmentTagsEnum {
  HOMEWORK = 11,
  EXTRA_PRACTICE = 12,
}

export enum MarkingSchemesEnum {
  NO_PARTIAL_MARKS = 1,
  SIMPLE_PARTIAL_MARKS = 2,
}

export enum ReplacementQuestionTypeEnum {
  EASIER = "Easier",
  HARDER = "Harder",
  SIMILAR = "Similar",
}

export enum QuestionPaperTemplateEnum {
  DEFAULT = "QP_DEFAULT",
  SIMPLE = "QP_SIMPLE",
}

export enum PdfColumnCountEnum {
  SINGLE = 1,
  DOUBLE = 2,
}

// Lifecycle of one activity-report send run. A fresh dispatch comes back PENDING/QUEUED —
// nothing has gone out yet.
export enum ActivityReportDispatchStatusEnum {
  PENDING = 1,
  QUEUED = 2,
  PROCESSING = 3,
  COMPLETED = 4,
  FAILED = 5,
}

// The two channels a report can go out on. Values double as the checkbox option ids in
// ModalSendActivityReport.
export enum ActivityReportChannelEnum {
  SMS = "Sms",
  EMAIL = "Email",
}

export enum ActivityDesignerModeEnum {
  DEFAULT = 1,
  TEST_SERIES_TEMPLATE = 2,
  TEST_SERIES_ACTIVITY = 3,
  PREDESIGNED_ACTIVITY = 4,
  PAST_YEAR_PAPER = 5,
}
